import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { directApiGet, directApiPost } from '../../services/api';
import { formatDisplayDateTime } from '../../utils/dateUtils';

interface ScheduledJob {
    id: string;
    name: string;
    next_run_time: string | null;
    trigger: string;
    schedule_description?: string;
}

/** API returns a JSON array; proxies/BFFs may wrap as `{ jobs: [...] }`. Anything else → []. */
function normalizeScheduledJobsPayload(data: unknown): ScheduledJob[] {
    if (Array.isArray(data)) {
        return data as ScheduledJob[];
    }
    if (data && typeof data === 'object' && 'jobs' in data) {
        const inner = (data as { jobs: unknown }).jobs;
        if (Array.isArray(inner)) {
            return inner as ScheduledJob[];
        }
    }
    return [];
}

const ScheduledJobs = () => {
    const [jobs, setJobs] = useState<ScheduledJob[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchScheduledJobs = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await directApiGet('/scheduler/jobs');
            const raw = response.data;
            if (typeof raw === 'string') {
                console.error(
                    'Scheduled jobs: expected JSON array from /scheduler/jobs; got text (check API base URL / reverse proxy).',
                );
                setJobs([]);
                setError(
                    'Could not load scheduled jobs: the server returned an unexpected response. Verify the app is calling the API host, not the static site.',
                );
                return;
            }
            setJobs(normalizeScheduledJobsPayload(raw));
        } catch (err: any) {
            setError(err.message || 'Failed to fetch scheduled jobs');
            console.error('Error fetching scheduled jobs:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            setError(null);
            await fetchScheduledJobs();
        } catch (err: any) {
            setError(err.message || 'Failed to refresh scheduled jobs');
            console.error('Error refreshing scheduled jobs:', err);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchScheduledJobs();
    }, []);

    const getJobStatus = (nextRunTime: string | null) => {
        if (!nextRunTime) {
            return { label: 'Inactive', color: 'default' as const };
        }
        const now = new Date();
        const nextRun = new Date(nextRunTime);
        if (nextRun > now) {
            return { label: 'Scheduled', color: 'success' as const };
        }
        return { label: 'Pending', color: 'warning' as const };
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <TitleBar />
            <Container maxWidth="xl" sx={{ pt: 6, pb: 8 }}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                mb: 1,
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text'
                            }}
                        >
                            Scheduled Jobs
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            View and manage automated review schedules
                        </Typography>
                    </Box>
                    <Tooltip title="Refresh scheduler and reload jobs">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={refreshing}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                },
                                '&:disabled': {
                                    bgcolor: 'grey.300',
                                }
                            }}
                        >
                            {refreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : jobs.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <ScheduleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No scheduled jobs found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Scheduled review jobs will appear here once configured
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            Job ID
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            Job Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            Schedule
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            Cron Expression
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            Next Run Time
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobs.map((job) => {
                                        const status = getJobStatus(job.next_run_time);
                                        return (
                                            <TableRow
                                                key={job.id}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: 'grey.50',
                                                    },
                                                    transition: 'background-color 0.2s'
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.8rem',
                                                            color: 'text.secondary'
                                                        }}
                                                    >
                                                        {job.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {job.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            fontWeight: 500, 
                                                            color: 'text.primary',
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: 1.8
                                                        }}
                                                    >
                                                        {job.schedule_description || job.trigger}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontSize: '0.8rem',
                                                            bgcolor: 'grey.100',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        {job.trigger}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <ScheduleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                        <Typography variant="body2">
                                                            {job.next_run_time ? formatDisplayDateTime(job.next_run_time) : 'Not scheduled'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={status.label}
                                                        color={status.color}
                                                        size="small"
                                                        sx={{ fontWeight: 500 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                {jobs.length > 0 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Total scheduled jobs: {jobs.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Last updated: {new Date().toLocaleTimeString()}
                        </Typography>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default ScheduledJobs;
